/**
 * Schema-built canned prompts for ICS-202 Draft with AI. Skips auto boxes,
 * slotted editors, and omitted sources.
 */

import {
  getFormSectionSchema,
  pratusSection,
  sectionHeading,
  type FormSection,
} from './formSectionSchema';
import { COMMUNITY_LIFELINES } from 'components/IAP/planningView/widgets/ics202BoxLabels';

export const ICS_202_BASE_ID = 'ICS_202';

/**
 * Skill the parent agent dispatches (same prefix as useAICommands
 * skillToCommand).
 */
export const ICS_202_DRAFT_SKILL = 'ics_202_draft';

export const ICS_202_SLOT_SOURCES = new Set(['objectives', 'attached']);

export const ICS_202_OMIT_SOURCES = new Set(['otherAttachmentsText']);

export const ICS_202_EXTRA_FIELDS: ReadonlyArray<readonly [string, string]> = [
  ['priorities', 'Incident Priorities'],
  [
    'criticalInformationRequirements',
    'Critical Information Requirements (CIRs)',
  ],
  ['keyDecisionsAndProcedures', 'Key Decisions & Procedures'],
  ['limitationsAndConstraints', 'Limitations & Constraints'],
];

/**
 * Coast Guard boxes 4–13 except box 10 (the signature). Box 6 is objective
 * rows; box 4 is the fixed lifeline list.
 */
export const USCG_ICS202_DRAFT_SOURCES = [
  'communityLifelines',
  'priorities',
  'objectives',
  'operationalPeriodCommandEmphasis',
  'siteSafetyPlan',
  'siteSafetyPlanLocation',
  'criticalInformationRequirements',
  'limitationsAndConstraints',
  'keyDecisionsAndProcedures',
] as const;

const USCG_DRAFT_LABELS: Record<
  (typeof USCG_ICS202_DRAFT_SOURCES)[number],
  readonly [string, FormSection['kind']]
> = {
  communityLifelines: [
    `4. Community Lifelines (${COMMUNITY_LIFELINES.join(', ')})`,
    'text',
  ],
  priorities: ['5. Incident Priorities', 'text'],
  objectives: ['6. Incident Objectives', 'table'],
  operationalPeriodCommandEmphasis: [
    '7. Operational Period Command Emphasis',
    'text',
  ],
  siteSafetyPlan: ['8. Site Safety Plan Required', 'boolean'],
  siteSafetyPlanLocation: ['9. Site Safety Plan located at', 'text'],
  criticalInformationRequirements: [
    '11. Critical Information Requirements',
    'text',
  ],
  limitationsAndConstraints: ['12. Limitations and Constraints', 'text'],
  keyDecisionsAndProcedures: ['13. Key Decisions and Procedures', 'text'],
};

export const listUscgIcs202DraftFields = (): Ics202DraftableField[] =>
  USCG_ICS202_DRAFT_SOURCES.map((source) => {
    const [label, kind] = USCG_DRAFT_LABELS[source];
    return {
      source,
      label,
      instruction: label,
      kind,
    };
  });

export type Ics202DraftableField = {
  source: string;
  label: string;
  instruction: string;
  kind: FormSection['kind'];
};

const isDraftableSection = (section: FormSection): boolean =>
  section.kind !== 'auto' &&
  !ICS_202_SLOT_SOURCES.has(section.source) &&
  !ICS_202_OMIT_SOURCES.has(section.source);

export const listIcs202DraftableFields = (): Ics202DraftableField[] => {
  const schema = getFormSectionSchema(ICS_202_BASE_ID);
  const printed = (schema?.sections ?? [])
    .filter(isDraftableSection)
    .map((section) => ({
      source: section.source,
      label: sectionHeading(section),
      instruction: section.printedLabel || section.label,
      kind: section.kind,
    }));

  const extras = ICS_202_EXTRA_FIELDS.map(([source, label]) => {
    const section = pratusSection(source, label);
    return {
      source,
      label,
      instruction: section.printedLabel,
      kind: section.kind,
    };
  });

  return [...printed, ...extras];
};

export const buildIcs202CannedPrompt = (sources?: string[]): string => {
  const allow = sources?.length ? new Set(sources) : null;
  const printed = listIcs202DraftableFields();
  const printedSources = new Set(printed.map((field) => field.source));
  const pool = [
    ...printed,
    ...listUscgIcs202DraftFields().filter(
      (field) => !printedSources.has(field.source)
    ),
  ];
  const fields = allow
    ? pool.filter((field) => allow.has(field.source))
    : printed;
  const lines = fields.map(
    (field) => `- ${field.label} (${field.source}): ${field.instruction}`
  );
  const objectiveLine = fields.some((field) => field.source === 'objectives')
    ? 'For objectives, propose new rows with a name and description. Do not remove objectives that already exist.'
    : 'Do not invent objectives or attachments.';
  return [
    'Draft new content for the ICS 202 Incident Objectives form for this operational period.',
    'Write only the listed boxes.',
    objectiveLine,
    'Ground the draft in current incident data and any governing organization documents.',
    'Keep command emphasis as the Incident Commander’s intent — specific, not generic.',
    '',
    'Boxes to draft:',
    ...lines,
  ].join('\n');
};

export const buildIcs202SkillPrompt = (sources?: string[]): string =>
  `Use the ${ICS_202_DRAFT_SKILL} skill: ${buildIcs202CannedPrompt(sources)}`;
